import type {
  DyspatchEvent,
  HoldClearedBy,
  HoldReason,
  ProjectBrief,
  SweepReport,
  Task,
  TaskSize,
  TaskStatus,
} from '@dyspatch/core';
import type { ReviewEntry } from '@/lib/api';
import { eventDetail } from '@/lib/event-kinds';
import { duration, minutesSince } from '@/lib/format';

/**
 * The derived collections the fleet overview is made of. Ported from the render
 * functions in `packages/hub/src/dashboard.ts`, behaviour first: the same
 * definitions of "busy", "needs you" and "held" the old page used, so the two
 * cannot quote different numbers for the same minute while both are served.
 */

/** Presence lives in its own module (see `presence.ts`); re-exported so views keep one import. */
export { HEARTBEAT_TIMEOUT_MS, type Presence, presenceOf } from '@/lib/presence';

/** The Tasks page's groups live next door (see `task-groups.ts`), same trade. */
export {
  DEFAULT_TASK_RANGE,
  DEFAULT_TASK_STATUS,
  failures24h,
  isTaskRange,
  isTaskStatusFilter,
  olderFailures,
  TASK_RANGES,
  TASK_STATUS_FILTERS,
  type TaskRange,
  type TaskStatusFilter,
  taskPageGroups,
} from '@/lib/task-groups';

/**
 * What a task's size is, said in a word, and what the letter actually buys.
 *
 * `xs` / `s` / `m` / `l` is the planner's vocabulary and it was printed straight
 * through as "size s", which tells a reader the field exists and nothing else.
 * The size is a wall-clock budget: it sets the timeout after which the hub stops
 * waiting and asks a person what to do, and it sets how much of a five-hour
 * window a device must have left before the scheduler will offer the task at all.
 *
 * The minutes are core's `SIZE_TIMEOUT_MINUTES`, mirrored rather than imported
 * for the reason `event-kinds.ts` explains, and pinned by a test in
 * `packages/hub/test/web.test.ts`.
 */
export const SIZE_LABEL: Record<TaskSize, string> = {
  xs: 'tiny',
  s: 'small',
  m: 'medium',
  l: 'large',
};

export const SIZE_MINUTES: Record<TaskSize, number> = {
  xs: 10,
  s: 20,
  m: 45,
  l: 90,
};

export function sizeTitle(size: TaskSize): string {
  return `Size ${size}: the wall-clock budget the plan gave this task. After ${duration(
    SIZE_MINUTES[size],
  )} the hub stops waiting and asks a person to requeue or cancel it.`;
}

/**
 * Core's `ACTIVE_TASK_STATUSES`: the states in which a device actually holds
 * the row. Mirrored for the reason above, and pinned by the same test.
 *
 * The graph's nodes read it (docs/JOB-LIFECYCLE.md 2.8). `assignedWorkerName`
 * is not cleared when a run ends, so a merged step whose line named a device
 * would read as a step still running on it.
 */
export const HELD_STATUSES: readonly TaskStatus[] = ['offered', 'claimed', 'running', 'reporting'];

/**
 * Core's `UNFINISHED_ON_A_DEVICE`, mirrored for the reason every constant in
 * this file is: importing the value pulls `node:crypto` into a browser bundle.
 * Pinned to core by a test, because the console refusing on a broader list than
 * the hub is what made the Retire button stop working with nothing on screen.
 */
export const RETIRE_BLOCKING_STATUSES: readonly TaskStatus[] = [...HELD_STATUSES, 'review'];

/**
 * What each device is still holding, by worker id, keyed and described the way
 * the hub's own refusal describes it.
 *
 * This is what the Retire button asks before it offers itself, so it has to
 * mean what the hub means. It did not: it counted every task that was not
 * terminal, which is `draft` and `queued` as well -- states in which no device
 * is holding anything. `assignedWorkerId` is not cleared when a run ends, so a
 * single requeued task was enough to refuse the retire forever, and
 * `GuardedButton` refuses by doing nothing, so the button simply stopped
 * working with no sentence anywhere on the screen.
 *
 * The safe direction to be wrong in is under-reporting: the verb is offered,
 * the hub refuses it, and the refusal is the same sentence with the
 * authoritative list in it. Over-reporting is the direction that strands
 * somebody, because the hub never gets asked. `api.tasks()` returning the
 * oldest 500 already puts this on the safe side of that line.
 */
export function heldByWorker(tasks: readonly Task[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const task of tasks) {
    if (!task.assignedWorkerId) continue;
    if (!RETIRE_BLOCKING_STATUSES.includes(task.status)) continue;
    const held = map.get(task.assignedWorkerId) ?? [];
    held.push(`${task.key} (${task.status})`);
    map.set(task.assignedWorkerId, held);
  }
  return map;
}

/** The task each worker is on, by worker id. `reporting` still counts as on it. */
export function runningByWorker(tasks: readonly Task[]): Map<string, Task> {
  const map = new Map<string, Task>();
  for (const task of tasks) {
    if (task.assignedWorkerId && (task.status === 'running' || task.status === 'reporting')) {
      map.set(task.assignedWorkerId, task);
    }
  }
  return map;
}

export function planApprovals(briefs: readonly ProjectBrief[]): ProjectBrief[] {
  return briefs
    .filter((brief) => brief.status === 'awaiting_approval')
    .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt));
}

/** Everything waiting on a person, and the two halves it is made of. */
export interface GateCounts {
  /** Plans drafted and waiting for a Go. */
  plans: number;
  /** Pull requests waiting for a review decision. */
  prs: number;
  total: number;
}

/**
 * The gate counts, derived once.
 *
 * There used to be two derivations of the same number and they disagreed for a
 * poll cycle at a time: the sidebar counted the briefs it had, the strip above it
 * counted `stats.briefs.awaitingApproval`, and `stats` and `briefs` are two
 * queries that refetch on their own schedules. So the Inbox said 4 while the tile
 * beside it said 3, which is the sort of thing that makes a person stop trusting
 * both numbers.
 *
 * Now every surface counts the same two lists: the briefs at the plan gate, and
 * the review queue itself rather than the metric beside it. Counting the queue
 * also means an optimistic write moves the counts with the row it removed,
 * because there is only one thing to move.
 */
export function gateCounts(
  briefs: readonly ProjectBrief[] | undefined,
  queue: readonly ReviewEntry[] | undefined,
): GateCounts {
  const plans = planApprovals(briefs ?? []).length;
  const prs = queue?.length ?? 0;
  return { plans, prs, total: plans + prs };
}

/** How long the oldest untouched gate has waited, in minutes. */
export function oldestGateMinutes(
  briefs: readonly ProjectBrief[],
  reviews: readonly ReviewEntry[],
  now = Date.now(),
): number {
  let oldest = 0;
  for (const brief of planApprovals(briefs)) {
    oldest = Math.max(oldest, minutesSince(brief.updatedAt, now));
  }
  for (const review of reviews) oldest = Math.max(oldest, review.waitingMinutes || 0);
  return oldest;
}

/**
 * Which step of its todo list a running agent is on, counted the way a person
 * counts: `done` is how many it has finished, so the one in hand is the next.
 *
 * Its own function because two surfaces print it and they must not disagree.
 * The task row and the "Now" line get it through `taskNote` below; the graph's
 * nodes (docs/JOB-LIFECYCLE.md 2.8) print the same number beside their bar. A
 * node reading "3 of 7" beside a sheet reading "step 4 of 7" is one fact told
 * twice in two numbers, which is worse than not drawing it.
 */
export function progressStep(progress: { done: number; total: number }): number {
  return Math.min(progress.done + 1, progress.total);
}

/** What `prState` returns: three facts about a step's git history, or none. */
export type PrState = 'ready' | 'draft' | 'pushed' | null;

/**
 * Ready, draft or pushed-with-no-PR yet: the three things a step's branch and
 * PR fields can be telling a reader, and no more (issue #29). The worker opens
 * a draft PR on its first checkpoint and nothing clears `prUrl`/`prDraft` on a
 * later failure or requeue (`setTaskCheckpoint`, `packages/db/src/tasks.ts`),
 * so a held or failed step keeps whichever of these it last earned - which is
 * the point, since that is the branch and PR docs/JOB-LIFECYCLE.md decision 2
 * promises fourteen days on. A step with neither is not a fourth state, it is
 * nothing yet to report.
 */
export function prState(task: {
  prUrl?: string | null;
  prDraft?: boolean | null;
  branch?: string | null;
}): PrState {
  if (task.prUrl) return task.prDraft ? 'draft' : 'ready';
  if (task.branch) return 'pushed';
  return null;
}

/**
 * Why a queued step is not being offered, in the words a row prints it in.
 *
 * The hub writes the explanation for the journal, where a line has to name its
 * own subject: "T-2 is held: all 2 registered workers are offline". In a row
 * that already prints T-2 and the word "held", that prefix is the third time
 * the reader is told the same two facts, and it pushes the part that is
 * actually new off the end of the line. So the prefix comes off here.
 *
 * Its own function because the graph's nodes (docs/JOB-LIFECYCLE.md 2.8) print
 * the same fact and must not print it differently. Structural rather than over
 * a `Task`, because a graph node is not one.
 */
export function heldNote(task: {
  holdReason?: string | null;
  holdExplanation?: string | null;
}): string | null {
  if (!task.holdReason) return null;
  return (task.holdExplanation || task.holdReason).replace(/^\S+\s+is\s+held:\s*/i, '');
}

/**
 * Core's `HOLD_CLEARED_BY`, mirrored for the reason `HELD_STATUSES` is, and
 * pinned to core by the same test: what ends each hold, the world on its own or
 * one person pressing one verb.
 */
export const HOLD_CLEARED_BY: Record<HoldReason, HoldClearedBy> = {
  no_workers_registered: 'itself',
  no_workers_online: 'itself',
  no_worker_for_repo: 'itself',
  all_dnd: 'itself',
  all_at_capacity: 'itself',
  all_accounts_at_capacity: 'itself',
  all_cooling_down: 'itself',
  all_accounts_cooling_down: 'itself',
  all_below_headroom: 'itself',
  retry_wait: 'itself',
  worker_offline: 'itself',
  all_previously_tried: 'requeue',
  paused: 'resume',
};

/**
 * What clears this step's hold, or `null` for a step that has none.
 *
 * A reason the console does not know reads as `itself`: the hold is printed, the
 * reason with it, and no verb is offered. That is the safe direction. A hub a
 * version ahead can only ever cost the reader a button the API still accepts,
 * where guessing the other way would draw Try again on a hold it cannot clear -
 * the bug this whole distinction exists to fix.
 */
export function holdClearedBy(task: {
  holdReason?: string | null;
  pausedAt?: string | null;
}): HoldClearedBy | null {
  // A pause outranks whatever the sweep last wrote, the way `taskNote` reads it:
  // the verb is Resume from the moment it lands rather than a sweep later.
  if (task.pausedAt) return 'resume';
  if (!task.holdReason) return null;
  return HOLD_CLEARED_BY[task.holdReason as HoldReason] ?? 'itself';
}

/**
 * The fields `isHeld` and `holdNeedsAPerson` actually read. A plain `Task` is
 * one, but so is a graph node (`JobGraph`'s `GraphTask`), which is why the
 * question is asked over this shape rather than the wire type: a step's own
 * hold and a sibling's, looked up to describe an unmet dependency (`lib/
 * dependencies.ts`), are the same question asked of two different records.
 */
export interface HoldableTask {
  status: TaskStatus;
  holdReason?: string | null;
  pausedAt?: string | null;
}

/**
 * Held: queued, and the scheduler is refusing to offer it to anybody.
 *
 * Distinct from paused, which is a person holding it and has Resume as its verb.
 * True for every hold, transient or not, because a held step is not a step
 * waiting its turn and the row says so either way. Which of them gets a button
 * is `holdNeedsAPerson`.
 */
export function isHeld(task: HoldableTask): boolean {
  return task.status === 'queued' && Boolean(task.holdReason) && !task.pausedAt;
}

/**
 * The one hold a person can do anything about: `all_previously_tried`.
 *
 * Every other hold clears itself. No device is online, every slot is full, every
 * window is spent - the next sweep offers the step with nobody having touched
 * it, and requeueing it in the meantime clears an empty tried-worker list and
 * buys one more sweep of the same hold. Drawing Try again there tells the reader
 * something is wrong when nothing is (issue #12).
 *
 * Every place that offers the button asks this one question, so the job pane and
 * the task pane cannot disagree about what is stuck.
 */
export function holdNeedsAPerson(task: HoldableTask): boolean {
  return isHeld(task) && holdClearedBy(task) === 'requeue';
}

/**
 * The one line under a task's title. `held` is the important case: a queued task
 * the scheduler is refusing to offer used to look identical to one simply
 * waiting its turn, which is why the hold reason rides on the task record.
 */
export function taskNote(task: Task): string | null {
  // A pause outranks the hold the sweep writes for it and is read off the row
  // rather than the hold, so the line is right the moment the verb lands rather
  // than a sweep later (docs/JOB-LIFECYCLE.md 2.5).
  if (task.pausedAt && task.status !== 'running' && task.status !== 'review') {
    return `Paused by ${task.pausedBy ?? 'a person'}${
      task.pauseReason ? `: ${task.pauseReason}` : ''
    }. Resume to continue.`;
  }
  if (task.status === 'running' && task.pausedAt) {
    return task.pauseMode === 'finish'
      ? `finishing this attempt, then holding (paused by ${task.pausedBy ?? 'a person'})`
      : `pausing: the device stops within a heartbeat (paused by ${task.pausedBy ?? 'a person'})`;
  }
  if (task.status === 'running' && task.progress) {
    const current = task.progress.current ? ` · ${task.progress.current}` : '';
    return `step ${progressStep(task.progress)} of ${task.progress.total}${current}`;
  }
  // "held" is for the one hold a person has to end. A step nothing can take
  // because every device is busy is waiting, not stuck, and the word carries
  // most of what the reader needs before they read the reason after it.
  if (task.status === 'queued' && task.holdReason) {
    return `${holdClearedBy(task) === 'requeue' ? 'held' : 'waiting'} · ${heldNote(task)}`;
  }
  // Not the error text. A failure prints its error in the block that offers the
  // verbs, and the row, the "Now" line and that block are all on screen at once
  // on the Tasks page: three copies of the same paragraph, two of them clipped.
  // What the row can say that the block cannot is whether the retries are gone.
  if (task.status === 'failed') return `failed · attempt ${task.attempts} of ${task.maxAttempts}`;
  return null;
}

/**
 * Who last had this task, including after it died.
 *
 * `assignedWorkerId` is released the moment a task is requeued, and a task can
 * reach `failed` from `queued` with nothing assigned at all, so a failure that
 * matters is exactly the case where the field is empty and the pane read
 * "Worker: -". The journal still knows: every worker-side write is attributed to
 * the device that made it, newest first, so the first attributed event with a
 * device on it is the last device that held the task.
 */
export function lastWorkerName(task: Task, events: readonly DyspatchEvent[] = []): string | null {
  if (task.assignedWorkerName) return task.assignedWorkerName;
  for (const event of events) {
    if (event.workerId && event.actor) return event.actor;
  }
  return null;
}

/**
 * What the most recent failed attempt printed, or null.
 *
 * A task row carries one reason (`lastError`) and no evidence; the journal
 * carries both, per attempt, because a step that failed three times failed three
 * different ways. The banner is about the attempt that just ended, so this walks
 * the events newest first - they arrive that way - and takes the first failure
 * line that has a payload on it.
 */
export function lastFailureDetail(events: readonly DyspatchEvent[]): string | null {
  for (const event of events) {
    if (event.kind !== 'task.failed' && event.kind !== 'task.retried') continue;
    const detail = eventDetail(event.data);
    if (detail) return detail;
  }
  return null;
}

/**
 * What a hand-run sweep did, as one sentence for the toast.
 *
 * A button that answers "done" tells you it was pressed, not what happened, and
 * the honest answer here is usually "nothing", which is worth saying out loud: a
 * queue that does not move because every device is offline looks identical to a
 * scheduler that is not running. So the empty report gets a sentence of its own
 * rather than a success tick.
 *
 * The offers are named while there are few enough to read. Past three it is a
 * count, because a toast is one line and a list of eight task keys is not one.
 */
export function sweepSentence(report: SweepReport): string {
  const parts: string[] = [];
  if (report.offered.length <= 3) {
    for (const offer of report.offered)
      parts.push(`offered ${offer.taskKey} to ${offer.workerName}`);
  } else {
    parts.push(`offered ${report.offered.length} tasks`);
  }
  if (report.held.length) parts.push(`held ${plural(report.held.length, 'task')}`);
  if (report.withdrawn.length)
    parts.push(`withdrew ${plural(report.withdrawn.length, 'stale offer')}`);
  if (report.timedOut.length) parts.push(`timed out ${plural(report.timedOut.length, 'run')}`);
  if (report.reclaimed.length)
    parts.push(`reclaimed ${plural(report.reclaimed.length, 'task')} from an offline device`);
  if (!parts.length) return 'Sweep ran: nothing was ready to dispatch and nothing had run over.';
  return `Sweep: ${parts.join(', ')}.`;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** 0..1, or null when the agent reported no todo list for this task. */
export function progressFraction(task: Task | undefined): number | null {
  const progress = task?.progress;
  if (!progress?.total) return null;
  return Math.max(0, Math.min(1, progress.done / progress.total));
}

/**
 * The task board's groups, in the order a person cares about them. Every status
 * in core's `TaskStatus` lands in exactly one group or is deliberately dropped
 * (`done` and `cancelled` are history, not a fleet state). A timeout arrives as
 * `failed` with `lastError` set -- there is no separate status for it.
 */
export const TASK_GROUPS = [
  { key: 'review', label: 'Waiting on a human', statuses: ['review'] },
  { key: 'running', label: 'Running', statuses: ['running', 'reporting'] },
  {
    key: 'queued',
    label: 'Queued',
    statuses: ['queued', 'offered', 'claimed'],
  },
  { key: 'failed', label: 'Failed', statuses: ['failed'] },
  { key: 'draft', label: 'Draft', statuses: ['draft'] },
] as const satisfies readonly {
  key: string;
  label: string;
  statuses: readonly Task['status'][];
}[];

export function groupTasks(
  tasks: readonly Task[],
): { label: string; key: string; tasks: Task[] }[] {
  return TASK_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    tasks: tasks
      .filter((task) => (group.statuses as readonly string[]).includes(task.status))
      .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt)),
  }));
}

/** Every repo any task names, for the Tasks page filter. */
export function reposOf(tasks: readonly Task[]): string[] {
  return [...new Set(tasks.map((task) => task.repo).filter(Boolean))].sort();
}

/**
 * Which enabled repos a device has no checkout for (issue #101).
 *
 * The worker already says its repo list on every heartbeat
 * (`buildHeartbeat` in `packages/worker/src/daemon.ts`), so the hub already
 * has everything this needs - it is a plain set difference between the
 * registry and `worker.repos`, not a new signal from anywhere. A repo that is
 * disabled is left out on purpose: a device with no checkout of a repo
 * nobody may compose against any more has nothing to be told about.
 */
export function missingCheckouts(
  worker: { repos: readonly string[] },
  enabledRepos: readonly string[],
): string[] {
  return enabledRepos.filter((repo) => !worker.repos.includes(repo));
}
